import { useSuspenseQuery } from '@apollo/client';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Content,
  Divider,
  HStack,
  Header,
  PageTitle,
  SidebarTrigger,
  VStack,
} from '@eluve/components';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';
import { dynamicArtifactTemplateSchema } from '@eluve/llm-outputs';
import {
  DynamicArtifactTemplateBuilder,
  templateBuilderStore,
} from '@eluve/smart-blocks';

import { DynamicArtifactTemplatePreview } from './components/DynamicArtifactPreview';
import { PrefillTemplateSection } from './components/PrefillTemplateSection';
import { TemplateEditingPageHeaderButtons } from './components/TemplateEditingPageHeaderButtons';
import { getOutputTemplateQuery } from './operations';
import { useCreateTemplate } from './useCreateTemplate';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';

export const CloneOutputTemplatePage: FC = () => {
  const { outputTemplateId } = useParams() as { outputTemplateId: string };
  const navigate = useNavigate();
  const isOrganizationRoute = useIsOrganizationRoute();

  const { handleSubmitTemplate, handleSaveDraftTemplate } = useCreateTemplate();

  const [isValid, setIsValid] = useState(false);

  const {
    data: { llmOutputTemplatesByPk: outputTemplate },
  } = useSuspenseQuery(getOutputTemplateQuery, {
    variables: {
      id: outputTemplateId,
    },
  });

  const artifactTemplate = useMemo(() => {
    const parsed = dynamicArtifactTemplateSchema.safeParse(
      outputTemplate?.variants?.[0]?.template,
    );
    if (!parsed.success) {
      return undefined;
    }

    return parsed.data;
  }, [outputTemplate?.variants]);

  useEffect(() => {
    if (artifactTemplate) {
      templateBuilderStore.send({
        type: 'hydrateArtifact',
        artifactTemplate,
        outputType:
          outputTemplate?.llmOutputType ?? LlmOutputTypesLookup.SOAP_NOTE,
      });
    }
  }, [artifactTemplate, outputTemplate?.llmOutputType]);

  return (
    <>
      <Header>
        {isOrganizationRoute && <SidebarTrigger />}
        <PageTitle isFullWidth>Create Template</PageTitle>

        <TemplateEditingPageHeaderButtons
          submitDisabled={!isValid}
          saveDraftDisabled={!isValid}
          handleSaveDraft={handleSaveDraftTemplate}
          handleSubmit={handleSubmitTemplate}
          handleDeleteDraft={() => navigate('..')}
        />
      </Header>
      <Content padded>
        <HStack align="stretch" className="h-full divide-x">
          <VStack className="h-full min-w-0 flex-1 overflow-y-auto p-6" gap={6}>
            <PrefillTemplateSection />
            <Divider className="my-0" />
            <DynamicArtifactTemplateBuilder
              onIsValidChange={setIsValid}
              showJsonSchema
            />
          </VStack>
          <VStack className="h-full min-w-0 flex-1 overflow-y-auto bg-bg-tertiary p-6">
            <DynamicArtifactTemplatePreview />
          </VStack>
        </HStack>
      </Content>
    </>
  );
};
